import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const Privacy = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gray-50 py-12"
    >
      <Helmet>
        <title>Privacy Policy - Paradise-Performance</title>
        <meta name="description" content="Privacy policy explaining how we collect, use, and protect your personal information when you visit our website or use our services." />
              <link rel="canonical" href="https://paradise-performance.com/privacy" />
      </Helmet>

      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
          
          <div className="prose prose-gray max-w-none">
            <p className="text-sm text-gray-600 mb-6">Last Updated: January 1, 2024</p>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 mb-4">
                Paradise-Performance ("we," "our," or "us") respects your privacy and is committed to protecting your personal data. This privacy policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Personal Information You Provide</h3>
              <ul className="list-disc ml-6 mb-4 text-gray-700">
                <li>Name, email address, phone number, and mailing address</li>
                <li>Driver's license information for test drives</li>
                <li>Financial information for financing applications</li>
                <li>Employment and income information</li>
                <li>Vehicle trade-in information</li>
                <li>Communication preferences</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Information Automatically Collected</h3>
              <ul className="list-disc ml-6 mb-4 text-gray-700">
                <li>IP address and browser information</li>
                <li>Device type and operating system</li>
                <li>Pages visited and time spent on our website</li>
                <li>Referring website addresses</li>
                <li>Location data (with your permission)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">We use your information to:</p>
              <ul className="list-disc ml-6 mb-4 text-gray-700">
                <li>Process vehicle sales and service appointments</li>
                <li>Facilitate financing applications</li>
                <li>Evaluate trade-in vehicles</li>
                <li>Communicate about your inquiries and transactions</li>
                <li>Send promotional offers and newsletters (with your consent)</li>
                <li>Improve our website and services</li>
                <li>Comply with legal obligations</li>
                <li>Prevent fraud and enhance security</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Legal Basis for Processing</h2>
              <p className="text-gray-700 mb-4">We process your personal data based on:</p>
              <ul className="list-disc ml-6 mb-4 text-gray-700">
                <li><strong>Contract:</strong> To fulfill our contractual obligations when you purchase a vehicle or service</li>
                <li><strong>Consent:</strong> When you opt-in to receive marketing communications</li>
                <li><strong>Legal Obligation:</strong> To comply with laws and regulations</li>
                <li><strong>Legitimate Interest:</strong> To operate our business and improve our services</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Information Sharing</h2>
              <p className="text-gray-700 mb-4">We may share your information with:</p>
              <ul className="list-disc ml-6 mb-4 text-gray-700">
                <li><strong>Financial Institutions:</strong> For financing and credit applications</li>
                <li><strong>Insurance Companies:</strong> For insurance quotes and coverage</li>
                <li><strong>Government Agencies:</strong> For vehicle registration and compliance</li>
                <li><strong>Service Providers:</strong> Who help us operate our business</li>
                <li><strong>Manufacturers:</strong> For warranty and recall purposes</li>
                <li><strong>Third Parties:</strong> With your explicit consent</li>
              </ul>
              <p className="text-gray-700 mb-4">
                We do not sell, rent, or trade your personal information to third parties for their marketing purposes without your explicit consent.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Security</h2>
              <p className="text-gray-700 mb-4">
                We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc ml-6 mb-4 text-gray-700">
                <li>SSL encryption for data transmission</li>
                <li>Secure servers and databases</li>
                <li>Access controls and authentication</li>
                <li>Regular security audits</li>
                <li>Employee training on data protection</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Your Rights</h2>
              <p className="text-gray-700 mb-4">You have the right to:</p>
              <ul className="list-disc ml-6 mb-4 text-gray-700">
                <li>Access your personal data we hold</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your data (subject to legal requirements)</li>
                <li>Opt-out of marketing communications</li>
                <li>Restrict or object to certain processing</li>
                <li>Data portability</li>
                <li>Withdraw consent at any time</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Cookies, Tracking, and Advertising</h2>
              <p className="text-gray-700 mb-4">
                We use cookies and similar tracking technologies to enhance your experience on our website. For more information, please see our <Link to="/cookies" className="text-primary-600 hover:underline">Cookie Policy</Link>.
              </p>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Google Advertising & Analytics</h3>
              <p className="text-gray-700 mb-4">
                We use the following Google services, which may collect data through cookies and tracking pixels:
              </p>
              <ul className="list-disc ml-6 mb-4 text-gray-700">
                <li><strong>Google Analytics (GA4):</strong> We use Google Analytics to analyze website traffic. Google Analytics collects information such as how often users visit our site, what pages they visit, and what other sites they used prior to coming to our site. We use this information to improve our website. Google's ability to use and share information collected by Google Analytics is restricted by the <a href="https://www.google.com/analytics/terms/us.html" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">Google Analytics Terms of Use</a> and the <a href="https://policies.google.com/privacy" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a>.</li>
                <li><strong>Google Ads Conversion Tracking:</strong> We use Google Ads to display advertisements and track conversions (e.g., phone calls, form submissions). This involves cookies that allow Google to serve ads based on your prior visits to our website.</li>
                <li><strong>Google Remarketing:</strong> We may use Google remarketing tags to display ads to you on third-party websites after you have visited our site. You can opt out of Google's use of cookies by visiting <a href="https://adssettings.google.com" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">Google Ad Settings</a>.</li>
              </ul>
              <p className="text-gray-700 mb-4">
                You can opt out of Google Analytics tracking by installing the <a href="https://tools.google.com/dlpage/gaoptout" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Children's Privacy</h2>
              <p className="text-gray-700 mb-4">
                Our website and services are not intended for children under 16 years of age. We do not knowingly collect personal information from children under 16. If we discover we have collected information from a child under 16, we will promptly delete it.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Third-Party Links</h2>
              <p className="text-gray-700 mb-4">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies before providing any personal information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Data Retention</h2>
              <p className="text-gray-700 mb-4">
                We retain your personal data only as long as necessary to fulfill the purposes for which it was collected, comply with legal obligations, resolve disputes, and enforce our agreements. Retention periods vary based on:
              </p>
              <ul className="list-disc ml-6 mb-4 text-gray-700">
                <li>Legal requirements (e.g., tax records)</li>
                <li>Contractual obligations</li>
                <li>Legitimate business purposes</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. California Privacy Rights</h2>
              <p className="text-gray-700 mb-4">
                If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA), including:
              </p>
              <ul className="list-disc ml-6 mb-4 text-gray-700">
                <li>Right to know what personal information we collect</li>
                <li>Right to delete personal information</li>
                <li>Right to opt-out of the sale of personal information</li>
                <li>Right to non-discrimination</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Texas Privacy Rights</h2>
              <p className="text-gray-700 mb-4">
                If you are a Texas resident, you have additional rights under the Texas Data Privacy and Security Act (TDPSA, effective July 1, 2024), including:
              </p>
              <ul className="list-disc ml-6 mb-4 text-gray-700">
                <li>Right to confirm whether we are processing your personal data</li>
                <li>Right to access your personal data</li>
                <li>Right to correct inaccuracies in your personal data</li>
                <li>Right to delete your personal data</li>
                <li>Right to obtain a portable copy of your data</li>
                <li>Right to opt out of the processing of your data for targeted advertising, sale, or profiling</li>
              </ul>
              <p className="text-gray-700 mb-4">
                To exercise these rights, contact us using the information provided in the Contact Us section below. We will respond within 45 days of receiving your verified request.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Changes to This Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this privacy policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date. We encourage you to review this policy periodically.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have questions about this privacy policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg text-gray-700">
                <p className="font-semibold">Paradise-Performance</p>
                <p>305 Co Rd 3470</p>
                <p>Paradise, TX 76073</p>
                <p>Phone: (817) 382-2316</p>
                <p>Email: service@paradise-performance.com</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Privacy;
